import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArrowUpRight, Check, BellRing } from "lucide-react";

export default function FollowSignup() {
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "" });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Please provide an email address");
      return;
    }
    setBusy(true);
    try {
      await api.post("/followers", {
        ...form,
        notify_email: true,
      });
      toast.success("You're following the club! Email notifications enabled.");
      setTimeout(() => nav("/"), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicLayout>
      <div className="relative">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-2xl px-5 sm:px-6 lg:px-8 py-10 sm:py-16 lg:py-24">
          <span className="pill">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))]" /> Stay in the loop
          </span>
          <h1 className="mt-5 sm:mt-6 text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight leading-[1] text-balance">
            Follow the <span className="font-serif-display italic text-[hsl(var(--accent))]">club</span>.
          </h1>
          <p className="mt-4 text-muted-foreground text-pretty">
            Not ready to be a member? Subscribe to club updates. You&apos;ll automatically receive email updates about matches, events, and news.
          </p>

          <form className="mt-8 sm:mt-10 space-y-5" onSubmit={submit}>
            <Card className="p-5 sm:p-7 rounded-2xl space-y-5">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Contact</div>
              <div className="grid gap-5">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium">
                    Name <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="name"
                    data-testid="follow-name"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    className="mt-1.5 h-11 rounded-lg"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                  <Input
                    id="email"
                    data-testid="follow-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    className="mt-1.5 h-11 rounded-lg"
                    placeholder="you@example.com"
                  />
                </div>
              </div>
            </Card>

            <Card className="p-5 rounded-2xl bg-[hsl(var(--accent))]/5 border-[hsl(var(--accent))]/30">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-[hsl(var(--accent))] text-white flex items-center justify-center flex-shrink-0">
                  <BellRing className="h-4 w-4" />
                </div>
                <div className="text-sm">
                  <div className="font-semibold tracking-tight">Email updates are on by default</div>
                  <div className="text-muted-foreground mt-1">
                    By following the club you&apos;ll receive announcements, match updates and event invites.
                  </div>
                  <ul className="mt-3 space-y-1.5 text-xs">
                    <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[hsl(var(--accent))]" /> Email updates</li>
                    <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[hsl(var(--accent))]" /> Email updates</li>
                  </ul>
                </div>
              </div>
            </Card>

            <div className="flex items-center justify-between pt-2">
              <Button type="button" variant="outline" className="h-11 px-5 rounded-full" onClick={() => nav("/")}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy} className="h-11 px-6 btn-accent-modern" data-testid="follow-submit">
                {busy ? "Saving…" : (<>Start following <ArrowUpRight className="ml-1.5 h-4 w-4" /></>)}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </PublicLayout>
  );
}
